// ── UsagePage — which screens people actually use (Administrative → Usage) ──
//
// Administrators only (the API refuses everyone else). Reads api/getPageUsage,
// which aggregates dbo.PageViews: one row per visit to a screen, logged by the
// app itself — no third-party analytics, nothing leaves ACE.
//
// Counting starts the day tracking was deployed, so the header says how much
// history there is rather than letting a short window read as low usage.
// "Test as user" visits are never logged.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, SearchBar, SectionHeader, Spinner, STh, Td } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const WINDOWS = [{ d: 7, label: "7 days" }, { d: 30, label: "30 days" }, { d: 90, label: "90 days" }, { d: 365, label: "1 year" }];
const APP_LABEL = { upm: "Portfolio", sales: "Sales", collateral: "Collateral" };

// Route key → something readable. "all-loans" → "Loans", "sales-email-triage" →
// "Email Triage". Exact names where the key says little.
const NAMES = {
  "upm|dashboard": "Dashboard", "sales|sales-dashboard": "Sales Dashboard", "upm|account": "Account page",
  "upm|all-wftransactions": "Pmt Posting", "upm|reports": "Reports", "upm|audit": "Audit Log",
  "upm|users": "User Management", "upm|bank-mapping": "Bank Acct Mapping", "upm|client-bank-detail": "Client Bank Acct Detail",
  "upm|bank-transactions": "Internal Bank Accts", "upm|ucc-register": "UCC Filings", "upm|ucc-searches": "UCC Searches", "upm|usage": "Usage",
  "sales|sales-email-triage": "Email Review", "sales|sales-account": "Sales account page",
};
const pageName = (app, page) => NAMES[app + "|" + page] || String(page || "")
  .replace(/^(all|sales|integration)-/, "").replace(/^bi:/, "BI dashboard ")
  .split("-").map(w => w ? w[0].toUpperCase() + w.slice(1) : w).join(" ");

const dur = (s) => {
  s = Math.round(Number(s) || 0);
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60), h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m ${s % 60 ? (s % 60) + "s" : ""}`.trim();
};
const num = (n) => Number(n || 0).toLocaleString();

const Tile = ({ label, value, sub }) => (
  <div style={{ background: "var(--color-background-secondary)", border: "0.5px solid var(--color-border-tertiary)",
                borderRadius: 8, padding: "10px 14px", minWidth: 150, flex: "1 1 150px" }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
  </div>
);

const SortTable = ({ rows, cols, defaultKey, empty }) => {
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, defaultKey, "desc");
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr>{cols.map(c => <STh key={c.k} label={c.label} sortKey={c.k} currentKey={sortKey}
                                      currentDir={sortDir} onSort={toggle} numeric={c.numeric} />)}</tr></thead>
      <tbody>
        {sorted.length === 0 && <tr><Td colSpan={cols.length} style={{ color: "var(--color-text-secondary)", fontSize: 13 }}>{empty}</Td></tr>}
        {sorted.map((r, i) => <tr key={i}>{cols.map(c =>
          <Td key={c.k} style={{ textAlign: c.numeric ? "right" : "left", fontVariantNumeric: c.numeric ? "tabular-nums" : undefined,
                                 whiteSpace: "nowrap", ...(c.style ? c.style(r) : {}) }}>{c.render ? c.render(r) : r[c.k]}</Td>)}</tr>)}
      </tbody>
    </table>
  );
};

// ── Sign-ins and Exports (Usage tabs) ────────────────────────────────────────
// From api/getSecurityLog: every sign-in attempt (dbo.SignInEvent) and every
// Export CSV (dbo.ExportLog). Times are shown in this browser's time zone.
const OUTCOME = {
  "success":         { label: "Signed in",         bg: "#E1F5EE", fg: "#0F6E56" },
  "failed password": { label: "Wrong password",    bg: "#FDF0D5", fg: "#7A5200" },
  "failed code":     { label: "Wrong code",        bg: "#FDF0D5", fg: "#7A5200" },
  "locked out":      { label: "Locked out",        bg: "#FBE4DD", fg: "#993C1D" },
  "blocked network": { label: "Blocked — network", bg: "#FBE4DD", fg: "#993C1D" },
  "not provisioned": { label: "No ACE account",    bg: "#FBE4DD", fg: "#993C1D" },
};
const METHOD = { password: "Password", code: "Password + code", "trusted device": "Trusted device", microsoft: "Microsoft", session: "During session" };
// Server times are UTC without a zone marker; shown in local time.
const when = v => {
  if (!v) return "—";
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s.replace(" ", "T") + "Z");
  return isNaN(d) ? s : d.toLocaleString(undefined, { month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", minute: "2-digit" });
};
// "Chrome on Windows" from a user-agent string — enough to tell devices apart.
const device = ua => {
  const s = String(ua || ""); if (!s) return "—";
  const b = /Edg\//.test(s) ? "Edge" : /OPR\//.test(s) ? "Opera" : /Chrome\//.test(s) ? "Chrome" : /Firefox\//.test(s) ? "Firefox" : /Safari\//.test(s) ? "Safari" : "Browser";
  const o = /iPhone|iPad/.test(s) ? "iOS" : /Android/.test(s) ? "Android" : /Windows/.test(s) ? "Windows" : /Mac OS X/.test(s) ? "Mac" : /Linux/.test(s) ? "Linux" : "";
  return o ? `${b} on ${o}` : b;
};
const place = r => [r.city, r.region, r.country].filter(Boolean).join(", ") || "—";
const OutcomePill = ({ o }) => {
  const t = OUTCOME[o] || { label: o || "—", bg: "#F1EFE8", fg: "#5F5E5A" };
  return <span style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: t.bg, color: t.fg, whiteSpace: "nowrap" }}>{t.label}</span>;
};

const SecurityLog = ({ kind, days }) => {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("");
  const load = () => { setErr(null); setData(null);
    api.get("getSecurityLog", { kind, days }).then(setData).catch(e => setErr(e.message)); };
  useEffect(() => { load(); }, [kind, days]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!data) return <Spinner />;
  const all = data.rows || [];
  const match = r => !q || Object.values(r).join(" ").toLowerCase().includes(q.toLowerCase());

  if (kind === "exports") {
    const rows = all.filter(match).map(r => ({ ...r, pagename: r.page ? pageName(r.app || "upm", r.page) : "—" }));
    const people = new Set(all.map(r => r.userid)).size;
    const totalRows = all.reduce((t, r) => t + Number(r.rows || 0), 0);
    return <>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <Tile label="Exports" value={num(all.length)} sub={`last ${days} days`} />
        <Tile label="People" value={num(people)} />
        <Tile label="Rows exported" value={num(totalRows)} />
      </div>
      <Card style={{ padding: 0, overflowX: "auto" }}>
        <div style={{ padding: "12px 14px 4px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Exports, newest first</span>
          <span style={{ marginLeft: "auto" }}><SearchBar value={q} onChange={setQ} placeholder="Person, page, file, address…" /></span>
          <Btn onClick={() => exportCSV(rows, ["at", "username", "impersonatedby", "pagename", "filename", "rows", "columns", "ip"], "exports_log.csv")}>Export</Btn>
        </div>
        <SortTable rows={rows} defaultKey="at" empty="No exports in this window."
          cols={[
            { k: "at", label: "When", render: r => when(r.at) },
            { k: "username", label: "Person", style: () => ({ fontWeight: 500 }),
              render: r => <>{r.username || "—"}{r.impersonatedby && <span style={{ fontSize: 11, color: "#993C1D" }}> (by {r.impersonatedby}, testing)</span>}</> },
            { k: "pagename", label: "Page" },
            { k: "filename", label: "File" },
            { k: "rows", label: "Rows", numeric: true, render: r => num(r.rows) },
            { k: "ip", label: "Address" },
          ]} />
      </Card>
    </>;
  }

  const rows = all.filter(r => !only || (only === "new" ? r.newlocation : only === "problem" ? r.outcome !== "success" : r.outcome === only))
    .filter(match).map(r => ({ ...r, where: place(r), dev: device(r.useragent) }));
  const ok = all.filter(r => r.outcome === "success");
  const fails = all.filter(r => r.outcome !== "success");
  const fresh = all.filter(r => r.newlocation);
  return <>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      <Tile label="Sign-ins" value={num(ok.length)} sub={`${num(new Set(ok.map(r => r.userid)).size)} people, last ${days} days`} />
      <div style={{ flex: "1 1 150px", display: "flex", cursor: "pointer" }} onClick={() => setOnly(o => o === "new" ? "" : "new")}>
        <Tile label="New locations" value={num(fresh.length)} sub="somewhere the person hasn't signed in from in 180 days — click to list" /></div>
      <div style={{ flex: "1 1 150px", display: "flex", cursor: "pointer" }} onClick={() => setOnly(o => o === "problem" ? "" : "problem")}>
        <Tile label="Failed or blocked" value={num(fails.length)} sub={`${num(fails.filter(r => r.outcome === "blocked network").length)} blocked by network — click to list`} /></div>
    </div>
    <Card style={{ padding: 0, overflowX: "auto" }}>
      <div style={{ padding: "12px 14px 4px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Sign-ins, newest first</span>
        <select value={only} onChange={e => setOnly(e.target.value)} style={{ fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "0.5px solid #c8cdd2" }}>
          <option value="">Everything</option>
          <option value="new">New locations</option>
          <option value="problem">Failed or blocked</option>
          {Object.entries(OUTCOME).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span style={{ marginLeft: "auto" }}><SearchBar value={q} onChange={setQ} placeholder="Person, address, place…" /></span>
        <Btn onClick={() => exportCSV(rows, ["occurredat", "username", "outcome", "method", "ip", "city", "region", "country", "newlocation", "dev", "detail", "ipheaders"], "sign_ins.csv")}>Export</Btn>
      </div>
      <SortTable rows={rows} defaultKey="occurredat" empty="No sign-ins in this window."
        cols={[
          { k: "occurredat", label: "When", render: r => when(r.occurredat) },
          { k: "username", label: "Person", style: () => ({ fontWeight: 500 }) },
          { k: "outcome", label: "Result", render: r => <span title={r.detail || ""}><OutcomePill o={r.outcome} /></span> },
          { k: "method", label: "How", render: r => METHOD[r.method] || r.method || "—" },
          { k: "ip", label: "Address", render: r => <span title={r.ipheaders || ""}>{r.ip || "—"}</span> },
          { k: "where", label: "Approx. location", render: r => <>{r.where}{r.newlocation
            ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: "#7A5200", background: "#FDF0D5", padding: "1px 6px", borderRadius: 999 }}>new</span> : null}</> },
          { k: "dev", label: "Device" },
        ]} />
      <div style={{ padding: "6px 14px 12px", fontSize: 11.5, color: "var(--color-text-secondary)" }}>
        Location is looked up from the address, offline, and is approximate — a VPN shows the VPN&apos;s location. Hover an address for the raw headers it came from.
      </div>
    </Card>
  </>;
};

export const UsagePage = () => {
  const { api } = useApp();
  const [tab, setTab] = useState("pages");     // pages | signins | exports
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [appFilter, setAppFilter] = useState("");

  const load = (d = days) => {
    setErr(null);
    api.get("getPageUsage", { days: d }).then(setData).catch(e => setErr(e.message));
  };
  useEffect(() => { load(days); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <ErrorMsg msg={err} onRetry={() => load()} />;
  if (!data) return <Spinner />;

  const pages = (data.pages || []).map(p => ({ ...p, name: pageName(p.app, p.page), appname: APP_LABEL[p.app] || p.app,
                                               quiet: Number(p.visits) === 0 }))
    .filter(p => !appFilter || p.app === appFilter)
    .filter(p => !q || (p.name + " " + p.page).toLowerCase().includes(q.toLowerCase()));
  const used = pages.filter(p => !p.quiet);
  const quiet = pages.filter(p => p.quiet);
  const users = (data.users || []).map(u => ({ ...u, topname: u.toppage ? pageName("upm", u.toppage) : "—" }));
  const weekly = data.weekly || [];
  const totalVisits = used.reduce((t, p) => t + Number(p.visits || 0), 0);
  const totalSecs = used.reduce((t, p) => t + Number(p.seconds || 0), 0);
  const maxWeek = Math.max(1, ...weekly.map(w => Number(w.visits || 0)));
  const since = data.history && data.history.since;
  const apps = [...new Set((data.pages || []).map(p => p.app))];

  const tabs = <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
    {[["pages", "Pages"], ["signins", "Sign-ins"], ["exports", "Exports"]].map(([k, l]) =>
      <Btn key={k} variant={tab === k ? "primary" : undefined} onClick={() => setTab(k)}>{l}</Btn>)}
  </div>;
  if (tab !== "pages") return <>
    <SectionHeader title="Usage" actions={[
      <span key="w" style={{ display: "flex", gap: 4 }}>
        {WINDOWS.map(w => <Btn key={w.d} variant={days === w.d ? "primary" : undefined} onClick={() => setDays(w.d)}>{w.label}</Btn>)}
      </span>,
    ]} />
    {tabs}
    <SecurityLog kind={tab} days={days} />
  </>;

  return <>
    <SectionHeader title="Usage" actions={[
      <SearchBar key="q" value={q} onChange={setQ} placeholder="Page…" />,
      <select key="app" value={appFilter} onChange={e => setAppFilter(e.target.value)}
              style={{ fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "0.5px solid #c8cdd2" }}>
        <option value="">All apps</option>
        {apps.map(a => <option key={a} value={a}>{APP_LABEL[a] || a}</option>)}
      </select>,
      <span key="w" style={{ display: "flex", gap: 4 }}>
        {WINDOWS.map(w => <Btn key={w.d} variant={days === w.d ? "primary" : undefined} onClick={() => setDays(w.d)}>{w.label}</Btn>)}
      </span>,
      <Btn key="x" onClick={() => exportCSV(used, ["appname", "name", "page", "visits", "people", "avgsecs", "seconds", "lastopen"], "page_usage.csv")}>Export</Btn>,
    ]} />

    {tabs}
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12 }}>
      {since
        ? <>Counting since <strong>{fmtDate(since)}</strong> — {num(data.history.total)} visits on record. A page visit is one screen opened; time is how long it stayed open (tab hidden doesn&apos;t count). &ldquo;Test as user&rdquo; is never counted.</>
        : <>Nothing recorded yet. Counting starts once this version is deployed and people use the app; give it a couple of weeks before drawing conclusions.</>}
    </div>

    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      <Tile label="Visits" value={num(totalVisits)} sub={`last ${days} days`} />
      <Tile label="People" value={num(users.length)} sub="signed in and used a page" />
      <Tile label="Pages used" value={num(used.length)} sub={quiet.length ? `${quiet.length} not opened in this window` : null} />
      <Tile label="Time in app" value={dur(totalSecs)} />
    </div>

    {weekly.length > 0 && <Card style={{ padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Visits per week</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
        {weekly.map(w => (
          <div key={w.week} title={`Week of ${fmtDate(w.week)}: ${num(w.visits)} visits, ${num(w.people)} people`}
               style={{ flex: "1 1 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 18 }}>
            <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>{num(w.visits)}</div>
            <div style={{ width: "100%", maxWidth: 40, height: Math.max(2, (Number(w.visits) / maxWeek) * 80), background: B.cyan, borderRadius: "3px 3px 0 0" }} />
            <div style={{ fontSize: 10, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>{fmtDate(w.week).slice(0, 5)}</div>
          </div>))}
      </div>
    </Card>}

    <Card style={{ padding: 0, overflowX: "auto", marginBottom: 16 }}>
      <div style={{ padding: "12px 14px 4px", fontSize: 13, fontWeight: 600 }}>Pages, most used first</div>
      <SortTable rows={used} defaultKey="visits" empty="No page visits in this window."
        cols={[
          { k: "name", label: "Page", style: () => ({ fontWeight: 500 }) },
          { k: "appname", label: "App" },
          { k: "visits", label: "Visits", numeric: true, render: r => num(r.visits) },
          { k: "people", label: "People", numeric: true, render: r => num(r.people) },
          { k: "avgsecs", label: "Avg time", numeric: true, render: r => dur(r.avgsecs) },
          { k: "seconds", label: "Total time", numeric: true, render: r => dur(r.seconds) },
          { k: "lastopen", label: "Last opened", render: r => fmtDate(r.lastopen) },
        ]} />
    </Card>

    <Card style={{ padding: 0, overflowX: "auto", marginBottom: 16 }}>
      <div style={{ padding: "12px 14px 4px", fontSize: 13, fontWeight: 600 }}>People</div>
      <SortTable rows={users} defaultKey="visits" empty="Nobody in this window."
        cols={[
          { k: "username", label: "Person", style: () => ({ fontWeight: 500 }) },
          { k: "visits", label: "Visits", numeric: true, render: r => num(r.visits) },
          { k: "pages", label: "Pages", numeric: true },
          { k: "seconds", label: "Time", numeric: true, render: r => dur(r.seconds) },
          { k: "topname", label: "Most used page" },
          { k: "lastseen", label: "Last seen", render: r => fmtDate(r.lastseen) },
        ]} />
    </Card>

    {quiet.length > 0 && <Card style={{ padding: 0, overflowX: "auto" }}>
      <div style={{ padding: "12px 14px 4px", fontSize: 13, fontWeight: 600 }}>Not opened in the last {days} days</div>
      <div style={{ padding: "0 14px 6px", fontSize: 12, color: "var(--color-text-secondary)" }}>
        Pages used before but not in this window — candidates to simplify or retire. Pages never opened at all since tracking began don&apos;t appear anywhere.
      </div>
      <SortTable rows={quiet} defaultKey="lastopen" empty=""
        cols={[
          { k: "name", label: "Page", style: () => ({ fontWeight: 500 }) },
          { k: "appname", label: "App" },
          { k: "lastopen", label: "Last opened", render: r => fmtDate(r.lastopen) },
        ]} />
    </Card>}
  </>;
};
